import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  Injector,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subject,
} from 'rxjs';
import { IdbaLookupDataSource, IdbaLookupRequest } from './idba-lookup-data-source';
import { FieldErrorMessageComponent } from '../forms/field-error-message/field-error-message.component';
import { CadastroGenericoLookupService } from '../../services/generics/cadastro-generico-lookup.service';
import { IdbaLookupConfig, IdbaLookupId } from './idba-lookup-config';

type DisplayableLookupValue = {
  display?: (value: unknown) => string;
};

@Component({
  standalone: true,
  selector: 'idba-lookup-autocomplete',
  templateUrl: './idba-lookup-autocomplete.component.html',
  styleUrl: './idba-lookup-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    FieldErrorMessageComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => IdbaLookupAutocompleteComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => IdbaLookupAutocompleteComponent),
    },
  ],
})
export class IdbaLookupAutocompleteComponent<T>
  implements ControlValueAccessor, Validator, OnInit, OnChanges, AfterViewInit
{
  @Input({ required: true }) config!: IdbaLookupConfig<T, any>;

  readonly inputControl = new FormControl<T | string | null>(null);
  readonly validationMessages = {
    lookupOptionNotSelected: 'Selecione uma opção da lista ou limpe o campo.',
  };
  filteredOptions$: Observable<readonly T[]> = of([]);

  @ViewChild('autocompleteInput') private inputRef?: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) private trigger?: MatAutocompleteTrigger;

  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cadastroGenericoLookupService = inject(CadastroGenericoLookupService);
  private readonly injector = inject(Injector);
  private ngControl: NgControl | null = null;
  private lookupDataSource?: IdbaLookupDataSource<T>;
  private initialized = false;
  private shouldResolveSelectedId = true;
  private options: readonly T[] = [];
  private hasLoadedOptions = false;
  private onChange: (value: T | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private baseLookupParameters: Record<string, unknown> = {};
  private searchTerm: string | null = null;
  private readonly remoteSearchTerms = new Subject<string>();
  private readonly writtenValues$ = new Subject<T | null>();
  private hasSelectedOption = false;
  private onValidatorChange: () => void = () => undefined;
  private handlingUserInput = false;
  private parentFormContext: unknown = {};
  private parameterSignature = '';
  private readonly validationControls = new Set<AbstractControl>();

  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () => {
      const control = this.validationControl;
      return !!control && control.invalid && (control.touched || control.dirty);
    },
  };

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null, { self: true });

    this.subscribeToValidationControls();

    this.inputControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (typeof value === 'string') {
          const hadSelection = this.hasSelectedOption || this.shouldResolveSelectedId;
          this.shouldResolveSelectedId = false;
          this.config.events?.inputValueChange?.(value);
          this.hasSelectedOption = false;
          this.handlingUserInput = true;
          try {
            this.onChange(null);
            this.onValidatorChange();
            if (hadSelection) this.config.events?.optionCleared?.();
          } finally {
            this.handlingUserInput = false;
          }
          if (this.activeSearchMode === 'remote') {
            const search = value.trim();
            this.searchTerm = search;
            if (search.length < this.activeMinSearchLength) {
              this.reloadLookupOptions();
            } else {
              this.remoteSearchTerms.next(search);
            }
          }
        }
      });

    this.parentFormContext = this.currentFormValue;
    this.baseLookupParameters = this.activeLookupParameters;
    this.parameterSignature = JSON.stringify(this.baseLookupParameters);
    this.searchTerm = this.activeInitialSearchTerm == null ? null : String(this.activeInitialSearchTerm).trim();
    this.remoteSearchTerms
      .pipe(debounceTime(this.activeSearchDebounceMs), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((search) => {
        this.searchTerm = search;
        this.reloadLookupOptions();
      });

    this.lookupDataSource = new IdbaLookupDataSource<T>(
      (request) => this.loadLookupOptions(request),
      this.createLookupRequest(),
    );

    const options$ = this.lookupDataSource.options$;

    options$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => {
        this.options = options;
        this.hasLoadedOptions = true;
        this.selectInitialValue();
        this.autoSelectSingleLoadedOption();
        this.config.events?.optionsLoaded?.(options);
        this.changeDetectorRef.markForCheck();
      });

    const parentForm = this.ngControl?.control?.parent;
    const parentFormChanges$ = parentForm
      ? parentForm.valueChanges.pipe(startWith(parentForm.value))
      : of(null);

    parentFormChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.parentFormContext = (parentForm as { getRawValue?: () => unknown } | null)?.getRawValue?.() ?? value ?? {};
        if (!this.hasDynamicLookupParameters) return;
        const parameters = this.activeLookupParameters;
        const signature = JSON.stringify(parameters);
        if (signature === this.parameterSignature) return;
        this.parameterSignature = signature;
        this.baseLookupParameters = parameters;
        if (this.initialized) this.reloadLookupOptions();
      });
    this.filteredOptions$ = combineLatest([
      options$,
      merge(this.inputControl.valueChanges, this.writtenValues$).pipe(startWith(this.inputControl.value)),
      parentFormChanges$,
    ]).pipe(
      map(([options, value]) => this.filter(options, value, this.currentFormValue)),
    );

    this.initialized = true;
  }

  ngAfterViewInit(): void {
    this.subscribeToValidationControls();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['config'];
    if (!change) return;

    const previousConfig = change.previousValue as IdbaLookupConfig<T, any> | undefined;
    const currentId = this.activeInitialId;
    const previousId = previousConfig?.selection?.initialId ?? null;
    const initialIdChanged = change.firstChange || !Object.is(previousId, currentId);
    if (initialIdChanged) {
      this.shouldResolveSelectedId = currentId !== null && currentId !== undefined && currentId !== '';
      if (!this.shouldResolveSelectedId && !change.firstChange) {
        this.hasSelectedOption = false;
        this.inputControl.setValue(null, { emitEvent: false });
        this.onChange(null);
      }
    }

    this.searchTerm = this.activeInitialSearchTerm == null ? null : String(this.activeInitialSearchTerm).trim();
    this.baseLookupParameters = this.activeLookupParameters;
    this.parameterSignature = JSON.stringify(this.baseLookupParameters);
    if (this.hasLoadedOptions) this.selectInitialValue();
    if (this.initialized && !change.firstChange) {
      this.lookupDataSource?.reload(this.createLookupRequest(this.activeLookupParameters), true);
    }
  }
  displayValue = (value: T | string | null): string => {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    return this.resolveDisplay(value);
  };

  private get activeSearchMode(): 'local' | 'remote' {
    return this.config.search?.mode ?? 'local';
  }

  private get activeMinSearchLength(): number {
    return this.config.search?.minLength ?? 1;
  }

  private get activeSearchParameter(): string {
    return this.config.search?.parameter ?? 'search';
  }

  private get activeSearchDebounceMs(): number {
    return this.config.search?.debounceMs ?? 300;
  }

  private get activeInitialSearchTerm(): string | number | null {
    return this.config.search?.initialTerm ?? null;
  }

  private get activeAutoLoad(): boolean {
    return this.config.behavior?.autoLoad ?? true;
  }

  private get activeInitialId(): IdbaLookupId | null {
    return this.config.selection?.initialId ?? null;
  }

  private get activeIdKey(): string {
    return this.config.idKey;
  }

  private get activeDisplayProperty(): string | null {
    return this.config.display?.property ?? null;
  }

  private get activeDisplayWith(): ((value: T) => string) | null {
    return this.config.display?.with ?? null;
  }

  get effectiveFieldLabel(): string {
    return this.config.label;
  }

  get effectivePlaceholder(): string {
    return this.config.placeholder || this.config.label;
  }

  get minimumSearchLengthMessage(): string | null {
    const minLength = this.config.search?.minLength;
    const value = this.inputControl.value;

    if (
      this.activeSearchMode !== 'remote' ||
      minLength == null ||
      minLength < 1 ||
      this.shouldResolveSelectedId ||
      (value !== null && typeof value !== 'string')
    ) {
      return null;
    }

    const searchLength = typeof value === 'string' ? value.trim().length : 0;
    if (searchLength >= minLength) return null;

    const unidade = minLength === 1 ? 'caractere' : 'caracteres';
    return `Digite pelo menos ${minLength} ${unidade} para buscar.`;
  }

  get validationControl(): AbstractControl | null {
    return this.config.validation?.control ?? this.ngControl?.control ?? null;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    const value = this.inputControl.value;
    if (typeof value === 'string' && value.trim().length > 0 && !this.hasSelectedOption) {
      return { lookupOptionNotSelected: true };
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private subscribeToValidationControls(): void {
    const controls = [this.ngControl?.control, this.config.validation?.control];
    controls.forEach((control) => {
      if (!control || this.validationControls.has(control)) return;
      this.validationControls.add(control);
      control.events
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.changeDetectorRef.markForCheck());
    });
  }

  optionLabel(option: T): string {
    return this.resolveDisplay(option);
  }

  focusInput(): void {
    this.inputRef?.nativeElement.focus();
    setTimeout(() => this.trigger?.openPanel());
  }

  clearInput(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.shouldResolveSelectedId = false;
    this.hasSelectedOption = false;
    this.inputControl.setValue(null);
    this.onChange(null);
    this.onValidatorChange();
    this.onTouched();
    this.config.events?.inputValueChange?.('');
    if (this.activeSearchMode === 'remote') {
      this.searchTerm = '';
      this.reloadLookupOptions();
    }
    this.config.events?.optionCleared?.();
    this.focusInput();
  }

  selectOption(event: MatAutocompleteSelectedEvent): void {
    this.shouldResolveSelectedId = false;
    const selected = event.option.value as T;
    this.hasSelectedOption = true;
    this.inputControl.setValue(selected, { emitEvent: false });
    this.onChange(selected);
    this.onValidatorChange();
    this.markAsTouched();
    this.config.events?.optionSelected?.(selected);
  }

  markAsTouched(): void {
    this.onTouched();
  }

  handleBlur(event: FocusEvent): void {
    this.markAsTouched();
    this.config.events?.inputBlur?.(event);
  }

  handleKeydown(event: KeyboardEvent): void {
    this.config.events?.inputKeydown?.(event);
  }

  writeValue(value: T | null): void {
    if (value == null && this.handlingUserInput && typeof this.inputControl.value === 'string') {
      return;
    }

    this.hasSelectedOption = value !== null && value !== undefined;
    if (!this.hasSelectedOption && this.activeInitialId === null) {
      this.shouldResolveSelectedId = false;
    }
    this.inputControl.setValue(value, { emitEvent: false });
    this.writtenValues$.next(value);
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
    }
  }
  private get currentFormValue(): unknown {
    const parent = this.ngControl?.control?.parent as {
      value?: unknown;
      getRawValue?: () => unknown;
    } | null | undefined;
    return parent?.getRawValue?.() ?? parent?.value ?? this.parentFormContext;
  }

  private get activeLookupParameters(): Record<string, unknown> {
    const source = this.config.source;
    if ('parameters' in source) {
      const parameters = source.parameters;
      return (typeof parameters === 'function'
        ? parameters(this.currentFormValue as never)
        : parameters) ?? {};
    }
    return {};
  }

  private get hasDynamicLookupParameters(): boolean {
    const source = this.config.source;
    return 'parameters' in source && typeof source.parameters === 'function';
  }

  private filter(
    options: readonly T[],
    value: T | string | null,
    context: unknown = this.currentFormValue,
  ): readonly T[] {
    const search = this.displayValue(value).trim();
    let filteredOptions: readonly T[];
    const configuredFilter = this.config.behavior?.filterOptions;

    if (this.activeSearchMode === 'remote') {
      filteredOptions = options;
    } else if (configuredFilter) {
      filteredOptions = configuredFilter(search, options, context as never);
    } else if (!search) {
      filteredOptions = options;
    } else if (this.config.source.kind === 'service' && this.config.source.service.model) {
      filteredOptions = this.config.source.service.model.filter(search, options as any[]);
    } else {
      const normalizedSearch = search.toLocaleLowerCase('pt-BR');
      filteredOptions = options.filter((option) =>
        this.optionLabel(option).toLocaleLowerCase('pt-BR').includes(normalizedSearch),
      );
    }

    const compare = this.config.behavior?.compareOptions;
    return filteredOptions.slice().sort((a, b) =>
      compare
        ? compare(a, b)
        : this.resolveDisplay(a).localeCompare(this.resolveDisplay(b), 'pt-BR', { sensitivity: 'base' }),
    );
  }

  private resolveDisplay(value: T): string {
    const displayWith = this.activeDisplayWith;
    if (displayWith) {
      return displayWith(value) ?? '';
    }

    const displayProperty = this.activeDisplayProperty;
    if (displayProperty) {
      return String((value as Record<string, unknown>)[displayProperty] ?? '');
    }

    const display = (value as T & DisplayableLookupValue).display;
    return typeof display === 'function' ? display.call(value, value) ?? '' : String(value ?? '');
  }

  private loadLookupOptions(request: IdbaLookupRequest): Observable<readonly T[]> {
    if (!this.activeAutoLoad) return of([]);

    if (
      this.activeSearchMode === 'remote' &&
      (this.searchTerm ?? '').trim().length < this.activeMinSearchLength
    ) {
      return of([]);
    }

    const source = this.config.source;
    if (source.kind === 'custom') {
      return source.load({
        request,
        search: this.searchTerm,
        formValue: this.currentFormValue as never,
      });
    }
    if (source.kind === 'service') {
      return source.service.listForLookup(request.procedure, request.parameters) as Observable<readonly T[]>;
    }
    return this.cadastroGenericoLookupService
      .listByModuloAssunto(source.modulo, source.assunto) as Observable<readonly T[]>;
  }

  private createLookupRequest(
    parameters: Record<string, unknown> | null = this.baseLookupParameters,
  ): IdbaLookupRequest {
    const requestParameters = { ...(parameters ?? {}) };
    if (this.activeSearchMode === 'remote' && this.searchTerm != null) {
      requestParameters[this.activeSearchParameter] = this.searchTerm;
    }

    return {
      procedure: this.config.source.procedure || 'pi-listar',
      parameters: requestParameters,
    };
  }

  private reloadLookupOptions(): void {
    this.lookupDataSource?.reload(this.createLookupRequest());
  }

  private autoSelectSingleLoadedOption(): void {
    const autoSelect = this.config.behavior?.autoSelectSingleOption ?? false;
    if (
      !autoSelect ||
      this.activeSearchMode === 'remote' ||
      this.options.length !== 1 ||
      this.inputControl.value != null
    ) {
      return;
    }
    const selected = this.options[0];
    this.inputControl.setValue(selected, { emitEvent: false });
    this.onChange(selected);
    this.hasSelectedOption = true;
  }

  private selectInitialValue(): void {
    const initialId = this.activeInitialId;
    if (
      !this.shouldResolveSelectedId ||
      initialId === null ||
      initialId === undefined ||
      initialId === ''
    ) {
      return;
    }

    const selected = this.options.find((option) =>
      String((option as Record<string, unknown>)[this.activeIdKey]) === String(initialId),
    );

    if (selected) {
      this.shouldResolveSelectedId = false;
      this.hasSelectedOption = true;
      this.inputControl.setValue(selected, { emitEvent: false });
      this.onChange(selected);
      this.config.events?.optionResolved?.(selected);
    }
  }
}
